declare module '@mui/material/styles' {
  interface TypeBackground {
    darker: string
    light: string
    lightBlue: string
    primary: string
    card: string
  }

  interface TypeText {
    primary: string
    secondary: string
    disabled: string
    white: string
  }

  interface PaletteColor {
    medium?: string
  }

  interface Palette {
    neutral: {
      low: {
        pure: string
        light: string
        medium: string
        dark: string
      }
      high: {
        pure: string
        light: string
        medium: string
        dark: string
      }
    }
    text: {
      primary: string
      white: string
      secondary: string
      disabled: string
    }
    warningIcon: {
      main: string
    }
    booleanAuth: {
      main: string
      secondary: string
      contrastText: string
    }
    icon: {
      main: string
      secondary: string
      medium: string
      negative: string
      positive: string
    }
    criticalIcon: {
      main: string
      secondary: string
      medium: string
    }
    importantIcon: {
      main: string
      secondary: string
      medium: string
    }
    strengthIcon: {
      main: string
      secondary: string
    }
    tooltip: {
      main: string
      secondary: string
    }
    circleProgress: {
      main: string
      secondary: string
      medium: string
    }
    chart: {
      main: string
      secondary: string
      medium: string
      contrastText: string
    }
    chartLegend: {
      main: string
      secondary: string
      medium: string
    }
    link: {
      main: string
      secondary: string
      medium: string
    }
    riskLevel: {
      critical: string
      high: string
      moderate: string
      low: string
    }
    gray: {
      0: string
    }
    feedback: {
      positive: Record<number, string>
      warning: Record<number, string>
      negative: Record<number, string>
      orange: Record<number, string>
    }
    purple: {
      100: string
      500: string
      700: string
    }
  }
}

export default {
  primary: {
    25: '#F2F8FF',
    50: '#E6F0FE',
    75: '#01A1DA', // TO-DO: Define this color in the design system or remove it if not needed
    100: '#CCE1FC',
    200: '#99C4F9',
    300: '#66A6F6',
    400: '#3389F3',
    500: '#006BF0',
    600: '#0056C0',
    700: '#004090',
    800: '#0048CB', // TO-DO: Define this color in the design system or remove it if not needed
    900: '#001530',
  },
  gray: {
    0: '#FFFFFF',
    50: '#F9FAFB',
    75: '#B0B0B0', // TO-DO: Define this color in the design system or remove it if not needed
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1C2431',
    900: '#111827',
  },
  feedback: {
    positive: {
      50: '#F9FFFC',
      100: '#EFFBF5',
      200: '#B3E9CE',
      300: '#8CDDB5',
      400: '#66D29D',
      500: '#40C784',
      600: '#339F6A',
      700: '#26774F',
      800: '#1A5035',
      900: '#0D281A',
    },
    warning: {
      50: '#FFFEF6',
      100: '#FFF7CC',
      150: '#FFF7CC', // TO-DO: Define this color in the design system or remove it if not needed
      200: '#FFEF99',
      250: '#FFFADF', // TO-DO: Define this color in the design system or remove it if not needed
      300: '#FFE666',
      400: '#FFDE33',
      500: '#E8C200',
      600: '#CCAB00',
      700: '#998000',
      800: '#665600',
      900: '#332B00',
    },
    negative: {
      50: '#FFF8F9',
      100: '#FBECEF',
      150: '#F2CCD4', // TO-DO: Define this color in the design system or remove it if not needed
      200: '#E599A9',
      250: '#FFDEE1', // TO-DO: Define this color in the design system or remove it if not needed
      300: '#D8667D',
      400: '#CB3352',
      500: '#BE0027',
      600: '#98001F',
      700: '#720017',
      800: '#4C0010',
    },
    orange: {
      100: '#FDEBDE',
      500: '#EF7C24',
    },
  },

  // The design system's secondary/accent purple. Named `purple` rather than
  // `secondary` on purpose: MUI reserves `palette.secondary` and derives its `main`
  // from shade 500, so defining it here would repoint every existing
  // `color="secondary"` (3 call sites in apps/frontend) at a new hue. This ramp adds
  // colours without changing any.
  // Design tokens: --px-secondary-100 / -500 / -700.
  purple: {
    100: '#EBDDFB',
    500: '#9637E7',
    700: '#6A1FB0',
  },

  warningIcon: {
    main: '#665600',
  },
  booleanAuth: {
    main: '#006BF0',
    secondary: '#E6F0FE',
    contrastText: '#ffffff',
  },
  icon: {
    main: '#CCE1FC',
    secondary: '#665600',
    medium: '#D1D5DB',
    negative: '#F84545',
    positive: '#2FB05A',
  },
  criticalIcon: {
    main: '#BE0027',
    secondary: '#F2CCD4',
    medium: '#B3261E',
  },
  importantIcon: {
    main: '#FFD600',
    secondary: '#CCAB004D',
    medium: '#E8C300',
  },
  strengthIcon: {
    main: '#006BF0',
    secondary: '#CCE1FC',
  },
  tooltip: {
    main: '#F4F9FF',
    secondary: '#0048CB',
  },
  circleProgress: {
    main: '#00F896',
    secondary: '#E6FAF8',
    medium: '#0072FF',
  },
  chart: {
    main: '#006BF0',
    secondary: '#6B7280',
    medium: '#E5E7EB',
    contrastText: '#FFFFFF',
  },
  chartLegend: {
    main: '#006BF0',
    secondary: '#CB3352',
    medium: '#98001F',
  },
  link: {
    main: '#006BF0',
    secondary: '#9637E7',
    medium: '#0056C0',
    visited: '#9637E7',
  },
  riskLevel: {
    critical: '#CB3352',
    high: '#EF7C24',
    moderate: '#E8C200',
    low: '#339F6A',
  },
  background: {
    light: '#fafafa',
    lightBlue: '#F4F9FF',
    primary: '#0072FF',
  },
  text: {
    primary: '#000000',
    secondary: '#A3A3A3',
    white: '#FFFFFF',
    disabled: 'rgba(0, 0, 0, 0, 0.38)',
  },
}
